// The standard DAW "musical typing" layout (Ableton, Logic, FL Studio):
// the home row plays white keys, the row above plays black keys.
// Matched by physical key position (`code`), so it works on any layout.

export const NOTE_KEYS: Record<string, number> = {
  KeyA: 0, // C
  KeyW: 1, // C#
  KeyS: 2, // D
  KeyE: 3, // D#
  KeyD: 4, // E
  KeyF: 5, // F
  KeyT: 6, // F#
  KeyG: 7, // G
  KeyY: 8, // G#
  KeyH: 9, // A
  KeyU: 10, // A#
  KeyJ: 11, // B
  KeyK: 12, // C, one octave up
};

/** Letter printed on each on-screen key, by pitch class. */
export const KEY_LABELS = ['A', 'W', 'S', 'E', 'D', 'F', 'T', 'G', 'Y', 'H', 'U', 'J'];

export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const MIN_OCTAVE = 1;
export const MAX_OCTAVE = 7;

/** MIDI note for a key in a given octave, where octave 4 starts at middle C (60). */
export function noteFor(code: string, octave: number): number | null {
  const offset = NOTE_KEYS[code];
  if (offset === undefined) return null;
  return (octave + 1) * 12 + offset;
}

export const noteName = (note: number): string => `${NOTE_NAMES[note % 12]}${Math.floor(note / 12) - 1}`;

export type Command =
  | 'octave-down'
  | 'octave-up'
  | 'velocity-down'
  | 'velocity-up'
  | 'prev-preset'
  | 'next-preset'
  | 'record'
  | 'play-take'
  | 'help';

export const COMMAND_KEYS: Record<string, Command> = {
  KeyZ: 'octave-down',
  KeyX: 'octave-up',
  KeyC: 'velocity-down',
  KeyV: 'velocity-up',
  ArrowLeft: 'prev-preset',
  ArrowRight: 'next-preset',
  KeyR: 'record',
  Space: 'play-take',
  Slash: 'help',
};

interface Handlers {
  noteOn(note: number): void;
  noteOff(note: number): void;
  command(c: Command): void;
  octave(): number;
}

const TEXT_INPUTS = new Set(['text', 'search', 'email', 'url', 'tel', 'number', 'password']);

/** True only for fields that take typed text; checkboxes and file pickers don't count. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return true;
  return target instanceof HTMLInputElement && TEXT_INPUTS.has(target.type);
}

/** Wires computer-keyboard playing. Returns a function that releases all held keys. */
export function attachKeyboard(h: Handlers): () => void {
  // Remember which note each key started, so changing octave mid-hold
  // still releases the right note.
  const held = new Map<string, number>();

  const releaseAll = () => {
    for (const note of held.values()) h.noteOff(note);
    held.clear();
  };

  window.addEventListener('keydown', (e) => {
    // macOS never sends keyup for keys released while Cmd is down, so
    // release everything rather than risk a stuck note.
    if (e.key === 'Meta') releaseAll();
    if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
    if (document.querySelector('dialog[open]')) return;
    const note = noteFor(e.code, h.octave());
    if (note !== null) {
      e.preventDefault();
      if (e.repeat || held.has(e.code)) return;
      held.set(e.code, note);
      h.noteOn(note);
      return;
    }
    const cmd = COMMAND_KEYS[e.code];
    if (!cmd || e.repeat) return;
    // Let Space and arrows keep their normal meaning on focused controls.
    const t = e.target as HTMLElement | null;
    if ((cmd === 'play-take' && t?.closest('button, [role="slider"]')) ||
        (cmd.endsWith('preset') && t?.closest('[role="slider"]'))) return;
    if (cmd === 'help' && !e.shiftKey) return;
    e.preventDefault();
    h.command(cmd);
  });

  window.addEventListener('keyup', (e) => {
    const note = held.get(e.code);
    if (note === undefined) return;
    held.delete(e.code);
    h.noteOff(note);
  });

  // Don't leave notes hanging when the window loses focus mid-chord.
  window.addEventListener('blur', releaseAll);
  document.addEventListener('visibilitychange', () => document.hidden && releaseAll());
  return releaseAll;
}
